/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { CursorSignature } from '@/components/pdf/CursorSignature';
import { useSignaturePlacement, PlacedSignature, PlacementState } from './useSignaturePlacement';

interface PlacementContextType {
  placementMode: string | null;
  placementData: string;
  placementType: 'signature' | 'initials' | null;
  placementState: PlacementState;
  cursorPosition: { x: number; y: number };
  setPlacementMode: (id: string | null, type?: 'signature' | 'initials') => void;
  setPlacementData: (data: string) => void;
  startPlacing: (data: string, type: 'signature' | 'initials') => void;
  cancelPlacing: () => void;
  onSignaturePlaced: ((signature: PlacedSignature) => void) | null;
  setOnSignaturePlaced: (handler: ((signature: PlacedSignature) => void) | null) => void;
}

const PlacementContext = createContext<PlacementContextType | undefined>(undefined);

export const usePlacement = () => {
  const context = useContext(PlacementContext);
  if (!context) {
    throw new Error('usePlacement must be used within a PlacementProvider');
  }
  return context;
};

export const PlacementProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [placementMode, setPlacementModeState] = useState<string | null>(null);
  const [placementData, setPlacementData] = useState<string>('');
  const [placementType, setPlacementType] = useState<'signature' | 'initials' | null>(null);
  const [onSignaturePlacedHandler, setOnSignaturePlacedHandler] = useState<((signature: PlacedSignature) => void) | null>(null);

  const handlePlaced = useCallback((signature: PlacedSignature) => {
    if (onSignaturePlacedHandler) {
      onSignaturePlacedHandler(signature);
    }
    setPlacementModeState(null);
    setPlacementType(null);
    setPlacementData('');
  }, [onSignaturePlacedHandler]);

  const handleCancelled = useCallback(() => {
    setPlacementModeState(null);
    setPlacementType(null);
    setPlacementData('');
  }, []);

  const { state: placementState, cursorPosition, startPlacing, cancelPlacing } = useSignaturePlacement({
    onPlaced: handlePlaced,
    onCancelled: handleCancelled,
  });

  const setPlacementMode = useCallback((id: string | null, type?: 'signature' | 'initials') => {
    setPlacementModeState(id);
    setPlacementType(id ? (type || 'signature') : null);
  }, []);

  const setOnSignaturePlaced = useCallback((handler: ((signature: PlacedSignature) => void) | null) => {
    setOnSignaturePlacedHandler(() => handler);
  }, []);

  return (
    <PlacementContext.Provider
      value={{
        placementMode,
        placementData,
        placementType,
        placementState,
        cursorPosition,
        setPlacementMode,
        setPlacementData,
        startPlacing,
        cancelPlacing,
        onSignaturePlaced: onSignaturePlacedHandler,
        setOnSignaturePlaced,
      }}
    >
      {children}
      {/* Global cursor signature/initials ghost */}
      <CursorSignature 
        data={placementData}
        type={placementType}
        isActive={placementState === 'placing'}
        position={cursorPosition}
      />
    </PlacementContext.Provider>
  );
};

export type { PlacedSignature };
